// frontend/src/services/tenantStorage.js

import api from './api';

class TenantStorageService {
    constructor() {
        this.cache = new Map(); // Local cache for performance
        this.pendingWrites = new Map(); // Track pending writes to avoid conflicts
    }

    // ==================== TENANT STATE METHODS ====================

    /**
     * Get tenant state values
     * @param {string|string[]} keys - Single key or array of keys to retrieve
     * @returns {Promise<any>} - Single value or object with multiple values
     */
    async getState(keys) {
        // Online mode — return cached value if available (salesDate etc. don't change mid-session)
        if (!Array.isArray(keys) && this.cache.has(keys)) {
            return this.cache.get(keys);
        }
        if (Array.isArray(keys) && keys.every(k => this.cache.has(k))) {
            const result = {};
            keys.forEach(k => { result[k] = this.cache.get(k); });
            return result;
        }

        try {
            const keyString = Array.isArray(keys) ? keys.join(',') : keys;
            const response = await api.get(`/tenant-storage/state?keys=${keyString}`);

            if (Array.isArray(keys)) {
                // Update cache for multiple keys
                Object.entries(response.data).forEach(([key, value]) => {
                    this.cache.set(key, value);
                });
                return response.data;
            } else {
                // Single key
                const value = response.data[keys];
                this.cache.set(keys, value);
                return value;
            }
        } catch (error) {
            console.error('Error getting tenant state:', error);
            // Return cached value if available
            if (Array.isArray(keys)) {
                const result = {};
                keys.forEach(key => {
                    if (this.cache.has(key)) {
                        result[key] = this.cache.get(key);
                    }
                });
                return result;
            } else {
                return this.cache.get(keys);
            }
        }
    }

    /**
     * Set tenant state values
     * @param {string|object} keyOrData - Single key or object with multiple key-value pairs
     * @param {any} value - Value to set (only if keyOrData is a string)
     * @returns {Promise<void>}
     */
    async setState(keyOrData, value) {
        let dataToSend;

        if (typeof keyOrData === 'string') {
            dataToSend = { [keyOrData]: value };
        } else {
            dataToSend = keyOrData;
        }

        // Update cache
        Object.entries(dataToSend).forEach(([key, val]) => {
            this.cache.set(key, val);
        });

        // Online mode - use API
        try {
            // Mark as pending
            Object.keys(dataToSend).forEach(key => {
                this.pendingWrites.set(key, Date.now());
            });

            await api.post('/tenant-storage/state', dataToSend);

            // Clear pending status
            Object.keys(dataToSend).forEach(key => {
                this.pendingWrites.delete(key);
            });
        } catch (error) {
            console.error('Error setting tenant state:', error);
            throw error;
        }
    }

    /**
     * Remove a tenant state key
     * @param {string} key - Key to remove
     * @returns {Promise<void>}
     */
    async removeState(key) {
        // Online mode - use API
        try {
            // Encoded, not interpolated raw — see orderKeyPathEncoding.test.js.
            // State keys are composed, not opaque: the daily order counter is
            // `daily_order_${diningOption}_${salesDate}`, which carries a space
            // from a multi-word dining option and another from the full
            // timestamp sales date.
            await api.delete(`/tenant-storage/state/${encodeURIComponent(key)}`);
            this.cache.delete(key);
        } catch (error) {
            console.error('Error removing tenant state:', error);
            throw error;
        }
    }

    // ==================== TENANT ORDERS METHODS ====================

    async getOrders(salesDate = null) {
        // Online mode - use API (existing code)
        try {
            const params = salesDate ? { sales_date: salesDate } : {};
            const response = await api.get('/tenant-storage/orders', { params });
            return response.data;
        } catch (error) {
            console.error('Error getting tenant orders:', error);
            return { orders: {}, printed: {} };
        }
    }

    async saveOrder(orderKey, orderData, isPrinted = false, updateExisting = false, orderId = null, salesDate = null) {
        // Online mode - use API (existing code)
        try {
            const params = { is_printed: isPrinted };
            if (updateExisting && orderId) {
                params.update_existing = true;
                params.order_id = orderId;
            }
            if (salesDate) {
                params.sales_date = salesDate;
            }

            // Encoded, not interpolated raw: an order key is
            // `${diningOption}-${tableNumber}`, so it carries a space from any
            // multi-word dining option and a `#` from every no-table order
            // number ("Takeaway-#001"). A raw `#` opens a URL fragment and
            // truncates the path before the request is sent; a raw space
            // reaches the native POS's backend as a literal `+`. See
            // orderKeyPathEncoding.test.js.
            const response = await api.post(
                `/tenant-storage/orders/${encodeURIComponent(orderKey)}`, orderData, { params });
            return response.data;
        } catch (error) {
            console.error('Error saving tenant order:', error);
            throw error;
        }
    }

    async markOrderPrinted(orderKey, isPrinted, orderId = null) {
        try {
            const params = { is_printed: isPrinted };
            if (orderId) {
                params.order_id = orderId;
            }

            // Encoded — see saveOrder above. Worse here than there: a raw `#`
            // swallows the `/printed` suffix too, so the request lands on a
            // different route entirely.
            await api.put(
                `/tenant-storage/orders/${encodeURIComponent(orderKey)}/printed`, null, { params });
        } catch (error) {
            console.error('Error marking order as printed:', error);
            throw error;
        }
    }

    async markOrderSubmitted(orderKey, isSubmitted, orderId = null) {
        try {
            const params = { is_submitted: isSubmitted };
            if (orderId) {
                params.order_id = orderId;
            }

            // Encoded — see saveOrder above. Same `/submitted`-swallowing
            // hazard as markOrderPrinted.
            await api.put(
                `/tenant-storage/orders/${encodeURIComponent(orderKey)}/submitted`, null, { params });
        } catch (error) {
            console.error('Error marking order as submitted:', error);
            throw error;
        }
    }

    async deleteOrder(orderKey, orderId = null) {
        try {
            const params = orderId ? { order_id: orderId } : {};
            // Encoded — see saveOrder above.
            await api.delete(`/tenant-storage/orders/${encodeURIComponent(orderKey)}`, { params });
        } catch (error) {
            console.error('Error deleting tenant order:', error);
            throw error;
        }
    }

    async changeOrderTable(orderId, newOrderKey, newDiningOption, newTableNumber) {
        try {
            const encodedOrderId = encodeURIComponent(orderId);

            const response = await api.put(
                `/tenant-storage/orders/${encodedOrderId}/change-table`,
                null,
                {
                    params: {
                        new_order_key: newOrderKey,
                        new_dining_option: newDiningOption,
                        new_table_number: newTableNumber
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error changing order table:', error);
            throw error;
        }
    }

    async getItem(key) {
        const value = await this.getState(key);
        return value ? JSON.stringify(value) : null;
    }

    async setItem(key, value) {
        let parsedValue;
        try {
            parsedValue = JSON.parse(value);
        } catch {
            parsedValue = value;
        }
        await this.setState(key, parsedValue);
    }

    async removeItem(key) {
        await this.removeState(key);
    }

    // ==================== BUSINESS DAY HELPERS ====================

    /** 
     * Get business day state
     * @returns {Promise<object>}
     */
    async getBusinessDayState() {
        const state = await this.getState(['startOfDay', 'salesDate', 'isBusinessDay', 'startCash']);

        // Convert to expected format with proper type conversion
        return {
            //  FIX: Return BOTH startOfDay and startOfDayTime for backward compatibility
            startOfDay: state.startOfDay || null,
            startOfDayTime: state.startOfDay || null,
            salesDate: state.salesDate || null,
            //  FIX: Parse isBusinessDay as boolean (handles "true", "1", true, 1)
            isBusinessDay: state.isBusinessDay === true ||
                state.isBusinessDay === 'true' ||
                state.isBusinessDay === 1 ||
                state.isBusinessDay === '1',
            startCash: parseFloat(state.startCash) || 0
        };
    }

    /**
     * Set business day state
     * @param {object} state - Business day state object
     * @returns {Promise<void>}
     */
    async setBusinessDayState(state) {
        // Convert from expected format to stored format
        const dataToStore = {
            startOfDay: state.startOfDayTime || state.startOfDay,
            salesDate: state.salesDate,
            isBusinessDay: state.isBusinessDay,
            startCash: state.startCash || 0
        };
        await this.setState(dataToStore);
    }

    /**
     * Clear business day state
     * @returns {Promise<void>}
     */
    async clearBusinessDayState() {
        await this.setState({
            startOfDay: null,
            salesDate: null,
            isBusinessDay: false,
            startCash: 0
        });
    }
}

// Export singleton instance
const tenantStorage = new TenantStorageService();
export default tenantStorage;

// Export individual functions for easier migration
export const {
    getState,
    setState,
    removeState,
    getOrders,
    saveOrder,
    markOrderPrinted,
    markOrderSubmitted,
    deleteOrder,
    changeOrderTable,
    getItem,
    setItem,
    removeItem,
    getBusinessDayState,
    setBusinessDayState,
    clearBusinessDayState
} = tenantStorage;